import React, { useEffect, useMemo, useState } from 'react';
import DrawerShell from '@/components/DrawerShell';
import { Button } from '@/components/ui/button';
import { Layers, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { rosterApi } from '../api/roster.api';
import {
  countWeekdayMatches, periodBounds, isoDate, addDays, startOfWeek,
} from '../utils/rosterDates';
import RosterBulkAllocateFields from './RosterBulkAllocateFields';

function empty(track) {
  const p = track.presets?.[0];
  return {
    staff_id: '',
    shift_start: p?.start || '09:00',
    shift_end: p?.end || '13:00',
    location: track.defaultLocation || '',
    slot_minutes: 15,
    published: true,
    days_of_week: [0, 1, 2, 3, 4],
    scope: 'week',
    to_date: '',
  };
}

export default function RosterBulkAllocatePanel({
  open, onClose, track, staffList, specialisationFilter, preselectedStaffId,
  period, anchor, onSaved,
}) {
  const [form, setForm] = useState(() => empty(track));
  const [saving, setSaving] = useState(false);
  const accent = track.accent;

  const filtered = useMemo(() => {
    if (!specialisationFilter || !track.bookingAware) return staffList;
    return staffList.filter((d) => d.specialization === specialisationFilter);
  }, [staffList, specialisationFilter, track.bookingAware]);

  useEffect(() => {
    if (!open) return;
    const bounds = periodBounds(period === 'month' ? 'month' : 'week', anchor);
    setForm({
      ...empty(track),
      staff_id: preselectedStaffId || '',
      scope: period === 'month' ? 'month' : 'week',
      to_date: bounds.toIso,
    });
  }, [open, preselectedStaffId, period, anchor, track]);

  const selected = filtered.find((d) => d.id === form.staff_id)
    || staffList.find((d) => d.id === form.staff_id);

  const range = useMemo(() => {
    if (form.scope === 'custom') {
      const fromIso = isoDate(startOfWeek(anchor));
      return { fromIso, toIso: form.to_date || isoDate(addDays(startOfWeek(anchor), 6)) };
    }
    return periodBounds(form.scope === 'month' ? 'month' : 'week', anchor);
  }, [form.scope, form.to_date, anchor]);

  const previewCount = countWeekdayMatches(range.fromIso, range.toIso, form.days_of_week);

  const toggleDay = (v) => {
    setForm((f) => {
      const set = new Set(f.days_of_week);
      if (set.has(v)) set.delete(v); else set.add(v);
      return { ...f, days_of_week: Array.from(set).sort() };
    });
  };

  const save = async () => {
    if (!form.staff_id || !selected) {
      toast.error(`Select a ${track.staffNoun}`);
      return;
    }
    if (!form.days_of_week.length) {
      toast.error('Pick at least one weekday');
      return;
    }
    setSaving(true);
    try {
      const res = await rosterApi.recurring({
        role: track.id,
        staff_id: form.staff_id,
        staff_name: selected.name,
        specialisation: selected.specialization || '',
        shift_start: form.shift_start,
        shift_end: form.shift_end,
        location: form.location,
        slot_minutes: track.bookingAware ? (Number(form.slot_minutes) || 15) : 0,
        published: form.published,
        from_date: range.fromIso,
        to_date: range.toIso,
        days_of_week: form.days_of_week,
      });
      toast.success(
        `Allocated ${res.inserted} shifts${res.skipped_duplicates ? ` (${res.skipped_duplicates} skipped)` : ''}`,
      );
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk allocate failed');
    }
    setSaving(false);
  };

  return (
    <DrawerShell
      open={open} onClose={onClose} eyebrow={track.label} title="Bulk allocate"
      subtitle={`${range.fromIso} → ${range.toIso} · one ${track.staffNoun}, many days`}
      accent={accent} icon={<Layers className="h-5 w-5" />} width="md" testId="roster-bulk-drawer"
      footer={(
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} style={{ background: accent }}
            className="text-white" data-testid="roster-bulk-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Create ~{previewCount} shifts
          </Button>
        </>
      )}
    >
      <RosterBulkAllocateFields
        track={track} form={form} setForm={setForm} staffList={filtered}
        accent={accent} range={range} previewCount={previewCount}
        selected={selected} toggleDay={toggleDay}
      />
    </DrawerShell>
  );
}
